'use client';
import { useFormatter, useTranslations } from 'next-intl';
import { type FC, useMemo } from 'react';
import { BaseLineExtend } from '@/app/components/icons/BaseLineExtend';
import { BaseLineFaceswap } from '@/app/components/icons/BaseLineFaceswap';
import { BaseLineMotionControl } from '@/app/components/icons/BaseLineMotionControl';
import { BaseLineOneClickvideo } from '@/app/components/icons/BaseLineOneClickvideo';
import { BaseLineQuickEdit } from '@/app/components/icons/BaseLineQuickEdit';
import { BaseLineRecreate } from '@/app/components/icons/BaseLineRecreate';
import { BaseLineStyleTransfer } from '@/app/components/icons/BaseLineStyleTransfer';
import { BaseLineSubmit3 } from '@/app/components/icons/BaseLineSubmit3';
import { BaseLineUpscale } from '@/app/components/icons/BaseLineUpscale';
import { BaseLineVideoremake } from '@/app/components/icons/BaseLineVideoremake';
import { ProductLineAiVideo } from '@/app/components/icons/ProductLineAiVideo';
import { ProductLineApply1 } from '@/app/components/icons/ProductLineApply1';
import { ProductLineLoadingPic } from '@/app/components/icons/ProductLineLoadingPic';
import { ProductLinePic } from '@/app/components/icons/ProductLinePic';
import { ProductLineReference } from '@/app/components/icons/ProductLineReference';
import { ProductLineReferenceToImage } from '@/app/components/icons/ProductLineReferenceToImage';
import { ProductLineTemplates } from '@/app/components/icons/ProductLineTemplates';
import { ProductLineText } from '@/app/components/icons/ProductLineText';
import { ProductLineText2Image } from '@/app/components/icons/ProductLineText2Image';
import type { ActionDialogProps } from '@/app/components/shared/ActionDialog';
import { CustomTooltip } from '@/app/components/shared/CustomTooltip';
import { ExtendPromptsDisplay } from '@/app/components/shared/ExtendPromptsDisplay';
import { ImageApplyButton } from '@/app/components/shared/ImageApplyButton';
import { ImageWithPlaceholder } from '@/app/components/shared/ImageWithPlaceholder';
import { MediaPromptsDisplay } from '@/app/components/shared/MediaPromptsDisplay';
import { TaskSettingTags } from '@/app/components/shared/TaskSettingTags';
import { TextWithTags } from '@/app/components/shared/TextWithTags';
import { VideoApplyButton } from '@/app/components/shared/VideoApplyButton';
import { Button } from '@/app/components/ui/button';
import { TYPE_KEY_MAP } from '@/app/constants';
import { useDevice, useGlobalModal, useIsScreen } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { CreationInfo, CreationWithTask, RecaptionPrompt, TaskInfo, TaskInputPrompts } from '@/app/types';
import {
  cn,
  isAdmissionTemplateTask,
  isExtendTask,
  isImageTask,
  isNoRecreateTask,
  isPostDisabledModelVersion,
  isRecreateModelVersionUnavailable,
  reportEvent,
  titleCaseToCamelCase,
} from '@/app/utils';
import { useTaskCard } from '../hooks/use-task-card';
import {
  getClawPassBadgeClassName,
  getImageButtonConfig,
  getVideoButtonConfig,
  isFailedOrCanceledTask,
} from '../utils';
import { CancelButton } from './CancelButton';
import { CreationActionButtons } from './CreationActionButtons';
import { VisualCreationCard } from './VisualCreationCard';

interface VisualTaskCardProps {
  task: TaskInfo;
  onOpenDetail: (creationId: CreationWithTask) => void;
  scrollToTop?: () => void;
  isTemplates: boolean;
}

export const VisualTaskCard: FC<VisualTaskCardProps> = ({ task, onOpenDetail, scrollToTop, isTemplates = false }) => {
  const t = useTranslations();
  const { updateCreation, isTeamSpace, clawPass } = useStore((state) => ({
    updateCreation: state.updateCreation,
    isTeamSpace: state.isTeamSpace,
    clawPass: state.clawPass,
  }));
  const { open: openActionDialog } = useGlobalModal('actionDialog');
  const { open: openPostsModal } = useGlobalModal('postsModal');
  const { isMobile } = useDevice();
  const { isLgScreen } = useIsScreen();

  const {
    taskState,
    hasFinished,
    canCancelTask,
    queueWaitTime,
    isWaitTimeProlonged,
    handleCancelTask,
    handleSelectedAction,
    handleRecreateAction,
  } = useTaskCard({ task, scrollToTop });

  const taskHistoryContainer = document.getElementById('taskHistoryContainer');
  const { input, type, id: taskId, created_at, settings } = task;
  const creations = task.creations ?? [];
  const creationCardCount = settings?.sample_count ?? 1;
  const mediaPrompts =
    input.prompts?.filter((p) => p.type === 'image' || p.type === 'material' || p.type === 'video') || [];

  const format = useFormatter();
  const date = new Date(created_at);
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const key = (type === 'upscale' || type === 'upscale_image') && task.origin_task_type ? task.origin_task_type : type;
  const maxVisibleSettingCount = key === 'music_video' ? 5 : 3;

  const clawPassBadgeClassName = getClawPassBadgeClassName(date, clawPass);

  // 根据任务类型渲染图标
  const renderTitleIcon = (taskType: TaskInfo['type']) => {
    switch (taskType) {
      case 'short_story':
      case 'character2video':
        return <ProductLineReference className="size-5" />;
      case 'reference2image':
        return <ProductLineReferenceToImage className="size-5" />;
      case 'text2image':
        return <ProductLineText2Image className="size-5" />;
      case 'headtailimg2video':
      case 'img2video':
      case 'multi_frame':
        return <ProductLineLoadingPic className="size-5" />;
      case 'text2video':
        return <ProductLineText className="size-5" />;
      case 'scene':
        return <ProductLineTemplates className="size-5" />;
      case 'video2extail':
      case 'video2extend':
        return <BaseLineExtend className="size-5" />;
      case 'image_edit':
      case 'video_edit':
        return <BaseLineQuickEdit className="size-5" />;
      case 'upscale':
      case 'upscale_image':
        return <BaseLineUpscale className="size-5" />;
      case 'face_swap':
        return <BaseLineFaceswap className="size-5" />;
      case 'one_click':
        return <BaseLineOneClickvideo className="size-5" />;
      case 'music_video':
      case 'lip_sync':
        return <BaseLineOneClickvideo className="size-5" />;
      case 'remake':
        return <BaseLineVideoremake className="size-5" />;
      case 'style_transfer':
        return <BaseLineStyleTransfer className="size-5" />;
      case 'motion_control':
        return <BaseLineMotionControl className="size-5" />;
      default:
        return null;
    }
  };

  const continueCreationTextPrompt = useMemo(() => {
    const prompt = input.prompts?.find((p) => p.type === 'text' && !p.multi_frame?.range)?.content || '';

    return prompt;
  }, [input.prompts, type]);

  const textPrompt = useMemo(() => {
    const textPrompts = input.prompts?.filter((p) => p.type === 'text') || [];

    if (input.editor_mode === 'professional') {
      // 专业模式：处理 recaption prompt
      let prompt = textPrompts.map((p) => p.content)?.[0] || '';
      try {
        const recaptionPrompt = JSON.parse(prompt);
        prompt = Object.entries(recaptionPrompt)
          .map(
            ([key, value]) =>
              `${t(`create.proMode.${titleCaseToCamelCase(key) as keyof RecaptionPrompt}.title`)}: ${value}`,
          )
          .join('\n');
      } catch {}
      return prompt;
    }

    // 非专业模式：检查是否有多帧情况
    const multiFramePrompts = textPrompts.filter((p) => p.multi_frame);

    if (task.type === 'multi_frame' && multiFramePrompts.length > 0) {
      // 有多帧情况，格式化为 "Frame X - Frame Y: prompt"
      return multiFramePrompts
        .map((p) => {
          const multiFrame = p.multi_frame;
          let frameLabel = '';

          if (multiFrame?.range) {
            // 帧范围
            frameLabel = t('create.img2VideoForm.transition.label', {
              from: multiFrame.range.from ? multiFrame.range.from + 1 : 1,
              to: multiFrame.range.to ? multiFrame.range.to + 1 : 2,
            });
          }

          return `${frameLabel} ${p.content}`;
        })
        .join('; ');
    }

    // 没有多帧情况，返回第一个文本 prompt
    return textPrompts.map((p) => p.content)?.[0] || '';
  }, [input, t]);

  // 投稿处理
  const handlePosts = (creation: CreationInfo) => {
    openPostsModal({
      creation: { ...creation, task: task },
      mobileNextStep: isMobile,
      onClose: (is_asset) => updateCreation(taskId, creation.id, { is_asset }),
    });
    reportEvent('submit_click', {
      submit_position: 'tools-submit',
    });
  };

  // 处理对话框操作
  const handleOpenActionDialog = (dialogAction: ActionDialogProps) => {
    openActionDialog(dialogAction);
  };

  // 获取要显示的操作按钮列表
  const showActionList = isImageTask(task)
    ? getImageButtonConfig({
        task,
        creation: creations[0],
        isLgScreen,
        type: 'task',
      })
    : getVideoButtonConfig({
        task,
        creation: creations[0],
        isLgScreen,
        type: 'task',
      });
  const canSubmitCreation = task.extra_params?.is_my_voice !== true && !isAdmissionTemplateTask(task);

  const renderPrompt = () => {
    if (isTemplates) {
      const images: string[] = [];
      mediaPrompts.forEach((prompt: TaskInputPrompts) => {
        // 如果模版的 src_imgs 包含多个图片，每张图分别展示
        if (prompt.src_imgs?.length && prompt.src_imgs.length > 1) {
          (prompt.src_imgs || []).forEach((src: string) => {
            images.push(src);
          });
        } else {
          images.push(prompt.content);
        }
      });

      return (
        <div className={cn('flex flex-1 items-end gap-1')}>
          {images.map((img) => (
            <CustomTooltip
              key={`${taskId}-tmpl-img-${img}`}
              content={
                <img
                  src={img}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  className="max-h-[240px] max-w-full rounded-6 border border-system-white16 object-cover"
                />
              }
            >
              <ImageWithPlaceholder
                src={img}
                alt=""
                className="aspect-[3/4] w-10 rounded-6 border border-system-white16 object-cover"
              />
            </CustomTooltip>
          ))}
          <div className="ml-1 flex h-5 items-center text-base text-white">
            {task.extra_params?.template_type === 'image' ? <ProductLinePic /> : <ProductLineAiVideo />}
          </div>
          <div className="text-sm text-system-white">{task.extra_params?.scene_name || task.scene}</div>
          <div className="ml-1 text-sm text-system-white36">
            {format.dateTime(date, {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: 'numeric',
              timeZone,
            })}
          </div>
        </div>
      );
    }
    return (
      <div className="flex h-[40px] flex-1 items-center lg:h-9">
        {!isExtendTask(task?.type) && mediaPrompts?.length ? (
          <MediaPromptsDisplay
            className="mr-3 h-full"
            prompts={mediaPrompts}
            taskType={type}
            thumbnailClassName="inline-block aspect-square h-full rounded-2 object-cover"
            collisionBoundary={taskHistoryContainer}
            collisionLeft={0}
          />
        ) : null}

        {isExtendTask(task?.type) && task?.input.prompts?.length && (
          <ExtendPromptsDisplay prompts={task?.input.prompts} className="mr-3 h-full" />
        )}

        <div className="relative flex h-full w-full flex-1 items-center">
          <CustomTooltip
            collisionBoundary={taskHistoryContainer}
            contentClassName="overflow-y-auto max-h-[400px] [word-break:break-word]"
            collisionTop={52}
            showOnlyOnOverflow={true}
            maxLines={2}
            content={
              <>
                <TextWithTags text={textPrompt} prompts={task.input.prompts} />
                <TaskSettingTags
                  taskId={taskId}
                  taskType={key}
                  settings={settings}
                  sceneName={task.extra_params?.scene_name}
                  showTaskType={false}
                />
              </>
            }
            sideOffset={8}
            side="top"
            trigger="auto"
            triggerClassName="block w-full"
          >
            <TextWithTags text={textPrompt} prompts={task.input.prompts} />
            <TaskSettingTags
              taskId={taskId}
              taskType={key}
              settings={settings}
              sceneName={task.extra_params?.scene_name}
              showTaskType={false}
              maxVisibleCount={maxVisibleSettingCount}
              isHoverShowAll={true}
              className="items-bottom inline-flex select-none ps-2 align-bottom group-[.is-overflowing]:hidden"
            />
            <div className="lg:-bottom-0.5 pointer-events-none absolute right-0 bottom-0 flex h-[22px] opacity-0 transition-opacity duration-200 group-[.is-overflowing]:opacity-100">
              <div className="h-full w-4 bg-gradient-to-l from-[#1A1C20] to-transparent opacity-0 transition-opacity duration-200 group-[.is-overflowing]:opacity-100" />
              <TaskSettingTags
                taskId={taskId}
                taskType={key}
                settings={settings}
                sceneName={task.extra_params?.scene_name}
                showTaskType={false}
                maxVisibleCount={maxVisibleSettingCount}
                className="items-bottom inline-flex h-full select-none bg-[#1A1C20] align-bottom"
              />
            </div>
          </CustomTooltip>
        </div>
      </div>
    );
  };

  return (
    <div className="sm:rounded-12 sm:bg-system-bg04 sm:p-3" key={taskId}>
      {/* 任务标题 */}
      {!isTemplates && (
        <div className="flex items-center sm:hidden">
          <div className="size-7 rounded-full bg-system-white10 p-1">{renderTitleIcon(type)}</div>
          <p className="ml-3 text-sm text-system-white opacity-90">{t(`create.${TYPE_KEY_MAP[type]}`)}</p>
          <div className="ml-2 text-sm text-system-white36">
            {format.dateTime(date, {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: 'numeric',
              timeZone,
            })}
          </div>
          {settings?.schedule_mode === 'nopeak' && (
            <span className="scale-[.84] whitespace-nowrap rounded-6 bg-[#3EA3FF] bg-opacity-20 px-1 py-0.5 text-[#3EA3FF] text-xs">
              {t('create.createForm.settings.nonPeakMode.title')}
            </span>
          )}
          {settings?.schedule_mode === 'claw_pass' && (
            <span
              className={cn(
                'scale-[.84] whitespace-nowrap rounded-6 bg-opacity-20 px-1 py-0.5 text-xs',
                clawPassBadgeClassName,
              )}
            >
              {t('create.createForm.settings.clawPassMode.title')}
            </span>
          )}
        </div>
      )}

      {/* 提示词 */}
      <div
        className={cn(
          'mt-3 mb-4 ml-10 pr-3 text-sm text-system-white48 md:my-3 md:ml-0',
          creationCardCount === 1 && 'max-w-[800px]',
        )}
      >
        {renderPrompt()}

        {/* 分镜宫格图 */}
        {(task.stage_results ?? []).length > 0 && (
          <div className="mt-4 grid grid-cols-2 gap-1">
            {(task.stage_results ?? []).map((s) => (
              <ImageWithPlaceholder key={s.uri} src={s.uri ?? ''} alt="stage result" className="h-20" fullScreen />
            ))}
          </div>
        )}
      </div>

      <div className={cn('ml-10 md:mr-0 md:ml-0 md:max-w-full lg:mr-10', creationCardCount === 1 && 'max-w-[800px]')}>
        <div className={cn('grid gap-2', creationCardCount > 1 && 'grid-cols-2 md:grid-cols-1')}>
          {new Array(creationCardCount).fill(0).map((_, idx) => (
            <VisualCreationCard
              key={creations[idx]?.id || idx}
              creation={creations[idx]}
              creationsLength={creationCardCount}
              task={task}
              taskState={taskState}
              queueWaitTime={queueWaitTime}
              isWaitTimeProlonged={isWaitTimeProlonged}
              onOpenActionDialog={handleOpenActionDialog}
              onOpenDetail={onOpenDetail}
              onCancelTask={handleCancelTask}
              onSubmit={handlePosts}
            />
          ))}
        </div>

        <div className="mt-3 flex items-center justify-between sm:flex-row-reverse lg:mt-2">
          <div className="flex items-center gap-2">
            {isImageTask(task) &&
              creationCardCount === 1 &&
              hasFinished &&
              creations[0] &&
              !creations[0]?.err_info?.err_code && (
                <ImageApplyButton
                  noMarkUri={creations[0].nomark_uri || ''}
                  uri={creations[0].uri || ''}
                  onApplyToTool={(applyType) => {
                    reportEvent('apply_click', {
                      apply_type: applyType,
                      apply_position: 'create_task_card',
                    });
                  }}
                  backfillTextPrompt={continueCreationTextPrompt}
                  container={taskHistoryContainer}
                  creationId={creations[0].id || ''}
                  showFlyTrace
                  content={
                    <Button className="h-8 gap-1 rounded-8 bg-system-bg02 px-3 font-normal enabled:hover:bg-system-hover01">
                      <ProductLineApply1 className="text-base sm:hidden" />
                      {t('task.action.continueCreate')}
                    </Button>
                  }
                />
              )}

            {creations[0]?.type === 'video' &&
              creationCardCount === 1 &&
              hasFinished &&
              !creations[0]?.err_info?.err_code && (
                <VideoApplyButton
                  creation={creations[0]}
                  container={taskHistoryContainer}
                  titleText={t('task.action.continueCreate')}
                  content={
                    <Button className="h-8 gap-1 rounded-8 bg-system-bg02 px-3 font-normal enabled:hover:bg-system-hover01">
                      <ProductLineApply1 className="text-base sm:hidden" />
                      {t('task.action.continueCreate')}
                    </Button>
                  }
                />
              )}

            {/* 做同款按钮 */}
            {!isNoRecreateTask(type) &&
              !isRecreateModelVersionUnavailable(type, settings?.model_version) &&
              !isAdmissionTemplateTask(task) && (
                <Button
                  className="h-8 gap-2 rounded-8 bg-system-bg02 px-3 font-normal enabled:hover:bg-system-hover01 disabled:text-system-white24"
                  onClick={handleRecreateAction}
                  icon={<BaseLineRecreate className="text-base sm:hidden" />}
                >
                  {t('task.action.recreate')}
                </Button>
              )}

            {/* PC端批量生成时的取消按钮 */}
            {creationCardCount > 1 && canCancelTask && (
              <CancelButton
                className="bg-system-bg02 enabled:hover:bg-system-hover01 disabled:text-system-white24 sm:hidden"
                taskId={taskId}
                onCancel={handleCancelTask}
              />
            )}

            {/* 投稿按钮 */}
            {!isTeamSpace &&
              canSubmitCreation &&
              !isPostDisabledModelVersion(settings?.model_version) &&
              taskState === 'success' &&
              creationCardCount === 1 &&
              !(creations[0]?.err_info?.err_code ?? false) && (
                <Button
                  className="h-8 gap-2 rounded-8 bg-system-bg02 px-3 font-normal enabled:hover:bg-system-hover01 disabled:text-white/40 lg:hidden"
                  icon={
                    <BaseLineSubmit3
                      className={cn(
                        'text-base text-system-white',
                        creations[0]?.is_asset && 'text-system-white opacity-[.40]',
                      )}
                    />
                  }
                  disabled={!hasFinished || creations[0]?.is_asset}
                  onClick={() => creations[0] && handlePosts(creations[0])}
                >
                  {t(creations[0]?.is_asset ? 'task.action.submitted' : 'task.action.submit')}
                </Button>
              )}
          </div>

          {/* 卡片右下角的一排按钮 */}
          {(isFailedOrCanceledTask(task) || creationCardCount === 1) && (
            <div className="rounded-8 bg-system-bg02">
              <div
                className="flex h-full items-center aria-disabled:pointer-events-none aria-disabled:opacity-[.24] aria-disabled:sm:hidden"
                aria-disabled={!hasFinished}
              >
                <CreationActionButtons
                  creation={creations[0]}
                  task={task}
                  showActionList={showActionList}
                  onSubmit={handlePosts}
                  onSelectedAction={handleSelectedAction}
                />
              </div>
            </div>
          )}

          {/* 移动端取消按钮 */}
          {canCancelTask && (
            <CancelButton
              className="hidden bg-system-bg02 enabled:hover:bg-system-hover01 disabled:text-system-white24 sm:flex"
              taskId={taskId}
              onCancel={handleCancelTask}
            />
          )}
        </div>
      </div>
    </div>
  );
};
